import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminTabs from '@/features/admin/components/AdminTabs';
import Modal from '@/features/admin/components/AdminModal';
import {
  getQualitySummary,
  getQualityVideo,
  listQualityVideos,
  type QualityGrade,
  type QualityGradeFilter,
  type QualityIssue,
  type QualityKind,
  type QualityMetric,
  type QualitySort,
  type QualitySummary,
  type QualityVideo,
  type QualityVideoDetail,
  type QualityWeek,
} from '@/features/admin/services/qualityService';
import { usePaginatedList } from '@/shared/hooks/usePaginatedList';
import { Card, CardHeader } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { createDataTableColumns } from '@/shared/ui/dataTableFeatures';
import { ErrorBanner } from '@/shared/ui/ErrorBanner';
import { Pill, type PillTone } from '@/shared/ui/Pill';
import { Segmented } from '@/shared/ui/Segmented';
import { safeErrorMessage } from '@/shared/utils/errors';
import { describeReason, formatBand, formatValue, metricLabel } from '@/features/admin/utils/qualityFormat';

// Internal tool for improving videos (never shown to users). The grades come from the
// rubric in valoi-engine-pipeline's evals/quality: a FAIL means a hard requirement
// failed or a threshold metric is outside its acceptable band. Every completed video
// is listed, including ones nobody has scored yet.

const PAGE_SIZE = 200;

const GRADE_FILTERS: { value: QualityGradeFilter | ''; label: string }[] = [
  { value: '', label: 'All grades' },
  { value: 'fail', label: 'Fail' },
  { value: 'warn', label: 'Warn' },
  { value: 'pass', label: 'Pass' },
  { value: 'unscored', label: 'Not scored' },
];

const SORTS: { value: QualitySort; label: string }[] = [
  { value: 'worst', label: 'Worst first' },
  { value: 'newest', label: 'Newest first' },
];

function gradeTone(grade: QualityGrade | null): PillTone {
  if (grade === 'pass') return 'green';
  if (grade === 'warn') return 'warn';
  if (grade === 'fail') return 'danger';
  return 'neutral';
}

function GradePill({ grade }: { grade: QualityGrade | null }) {
  return (
    <Pill tone={gradeTone(grade)} dot>
      {grade === null ? 'Not scored' : grade.toUpperCase()}
    </Pill>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { dateStyle: 'medium' });
}

function MetricTable({ title, metrics }: { title: string; metrics: QualityMetric[] }) {
  if (metrics.length === 0) return null;
  return (
    <div className="quality-detail__section">
      <h3 className="quality-detail__heading">{title}</h3>
      <table className="quality-metrics">
        <thead>
          <tr>
            <th>Metric</th>
            <th>Value</th>
            <th>Grade</th>
          </tr>
        </thead>
        <tbody>
          {metrics.map((m) => (
            <tr key={m.name} title={m.why}>
              <td>
                <span>{metricLabel(m.name)}</span>
                {/* Bands under the name rather than in their own columns: five columns
                    did not fit the modal, and the one that fell off was Grade. */}
                <span className="quality-metrics__band">
                  {m.category === 'hard'
                    ? `required ${formatBand(m.acceptable, m.unit)}`
                    : `target ${formatBand(m.target, m.unit)} · acceptable ${formatBand(m.acceptable, m.unit)}`}
                </span>
                {m.detail && m.grade !== 'pass' && <span className="quality-metrics__band">Look at: {m.detail}</span>}
              </td>
              <td className="quality-metrics__num">{formatValue(m)}</td>
              <td>
                <GradePill grade={m.grade} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QualityDetail({ jobId, onClose }: { jobId: string; onClose: () => void }) {
  const [detail, setDetail] = useState<QualityVideoDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getQualityVideo(jobId)
      .then((d) => !cancelled && setDetail(d))
      .catch((err: unknown) => !cancelled && setError(safeErrorMessage(err, 'Unable to load this video.')));
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const hard = detail?.metrics.filter((m) => m.category === 'hard') ?? [];
  const thresholds = detail?.metrics.filter((m) => m.category !== 'hard') ?? [];

  return (
    <Modal title={detail?.title || 'Video quality'} onClose={onClose} size="wide">
      {error ? (
        <ErrorBanner message={error} />
      ) : !detail ? (
        <div className="loading-container">
          <div className="loading-spinner" />
        </div>
      ) : (
        <div className="quality-detail">
          <div className="quality-detail__meta">
            <GradePill grade={detail.grade} />
            <span>
              {detail.modelName ?? '—'} / {detail.style ?? '—'} · {formatDate(detail.createdAt)}
            </span>
            {detail.videoUrl && (
              <a href={detail.videoUrl} target="_blank" rel="noreferrer">
                Watch video
              </a>
            )}
          </div>
          {detail.grade === null ? (
            <p className="quality-detail__note">This video has not been scored yet.</p>
          ) : (
            <>
              <MetricTable title="Hard requirements" metrics={hard} />
              <MetricTable title="Thresholds" metrics={thresholds} />
              <p className="quality-detail__note">
                Scored {detail.scoredAt ? formatDate(detail.scoredAt) : '—'} with rubric {detail.rubricVersion ?? '—'}{' '}
                from: {detail.inputs.join(', ') || '—'}.
              </p>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

type QualityView = 'overview' | 'problems' | 'kinds' | 'videos';

const VIEWS: { value: QualityView; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'problems', label: 'Problems' },
  { value: 'kinds', label: 'Model & style' },
  { value: 'videos', label: 'Videos' },
];

function isView(value: string | null): value is QualityView {
  return VIEWS.some((v) => v.value === value);
}

function pct(n: number, of: number): string {
  return of ? `${Math.round((100 * n) / of)}%` : '—';
}

function OverviewView({ summary }: { summary: QualitySummary }) {
  const graded = summary.grades.pass + summary.grades.warn + summary.grades.fail;
  const tiles: {
    label: string;
    value: number;
    note: string;
    grade?: QualityGrade;
  }[] = [
    {
      label: 'Scored',
      value: summary.scored,
      note: `of ${summary.completed} completed videos`,
    },
    {
      label: 'Fail',
      value: summary.grades.fail,
      note: pct(summary.grades.fail, graded),
      grade: 'fail',
    },
    {
      label: 'Warn',
      value: summary.grades.warn,
      note: pct(summary.grades.warn, graded),
      grade: 'warn',
    },
    {
      label: 'Pass',
      value: summary.grades.pass,
      note: pct(summary.grades.pass, graded),
      grade: 'pass',
    },
  ];
  return (
    <>
      <div className="quality-tiles">
        {tiles.map((t) => (
          <Card key={t.label}>
            <div className="quality-tile__label">{t.grade ? <GradePill grade={t.grade} /> : t.label}</div>
            <div className="sli-big quality-tile__value">{t.value}</div>
            <div className="quality-tile__note">{t.note}</div>
          </Card>
        ))}
      </div>
      <Card className="admin-section-gap">
        <CardHeader title="Weekly trend" description="Grades by the week each video was made" />
        {summary.weekly.length === 0 ? (
          <p className="sli-empty">No scored videos</p>
        ) : (
          <DataTable columns={weekColumns} data={summary.weekly} getRowId={(w) => w.weekStart} />
        )}
      </Card>
    </>
  );
}

const weekHelper = createDataTableColumns<QualityWeek>();
const weekColumns = weekHelper.columns([
  weekHelper.accessor((w) => formatDate(w.weekStart), {
    id: 'week',
    header: 'Week of',
    enableColumnFilter: false,
    meta: { filterType: 'none', align: 'left' },
  }),
  weekHelper.accessor((w) => w.passed + w.warned + w.failed, {
    id: 'videos',
    header: 'Videos',
    enableColumnFilter: false,
    meta: { filterType: 'none', align: 'left' },
  }),
  weekHelper.accessor('passed', {
    header: 'Pass',
    enableColumnFilter: false,
    meta: { filterType: 'none', align: 'left' },
  }),
  weekHelper.accessor('warned', {
    header: 'Warn',
    enableColumnFilter: false,
    meta: { filterType: 'none', align: 'left' },
  }),
  weekHelper.accessor('failed', {
    header: 'Fail',
    enableColumnFilter: false,
    meta: { filterType: 'none', align: 'left' },
  }),
  weekHelper.display({
    id: 'bar',
    header: 'Share',
    enableColumnFilter: false,
    meta: { filterType: 'none', align: 'left' },
    cell: ({ row }) => {
      const w = row.original;
      const n = w.passed + w.warned + w.failed || 1;
      return (
        <span
          className="quality-bar quality-bar--wide"
          aria-label={`${w.passed} pass, ${w.warned} warn, ${w.failed} fail`}
        >
          <span className="quality-bar__pass" style={{ width: `${(100 * w.passed) / n}%` }} />
          <span className="quality-bar__warn" style={{ width: `${(100 * w.warned) / n}%` }} />
          <span className="quality-bar__fail" style={{ width: `${(100 * w.failed) / n}%` }} />
        </span>
      );
    },
  }),
]);

function ProblemsView({ summary }: { summary: QualitySummary }) {
  const columns = useMemo(() => {
    const h = createDataTableColumns<QualityIssue>();
    return h.columns([
      h.accessor((i) => metricLabel(i.metric), {
        id: 'metric',
        header: 'Metric',
        filterFn: 'includesString',
        meta: { align: 'left' },
      }),
      h.accessor((i) => (i.category === 'hard' ? 'Hard requirement' : 'Threshold'), {
        id: 'category',
        header: 'Type',
        filterFn: 'equals',
        meta: {
          align: 'left',
          filterType: 'select',
          filterOptions: [
            { label: 'Hard requirement', value: 'Hard requirement' },
            { label: 'Threshold', value: 'Threshold' },
          ],
        },
      }),
      h.accessor('fails', {
        header: 'Failing',
        enableColumnFilter: false,
        meta: { filterType: 'none', align: 'left' },
      }),
      h.accessor('warns', {
        header: 'Warning',
        enableColumnFilter: false,
        meta: { filterType: 'none', align: 'left' },
      }),
      h.accessor((i) => (summary.scored ? (100 * i.fails) / summary.scored : 0), {
        id: 'share',
        header: '% of videos failing',
        enableColumnFilter: false,
        meta: { filterType: 'none', align: 'left' },
        cell: (info) => `${info.getValue().toFixed(0)}%`,
      }),
    ]);
  }, [summary.scored]);
  return summary.topIssues.length === 0 ? (
    <div className="empty-state">
      <div className="empty-state__title">No problems flagged</div>
    </div>
  ) : (
    <DataTable columns={columns} data={summary.topIssues} getRowId={(i) => i.metric} />
  );
}

const kindHelper = createDataTableColumns<QualityKind>();
const kindColumns = kindHelper.columns([
  kindHelper.accessor((k) => k.modelName ?? '—', {
    id: 'model',
    header: 'Model',
    filterFn: 'includesString',
    meta: { align: 'left' },
  }),
  kindHelper.accessor((k) => k.style ?? '—', {
    id: 'style',
    header: 'Style',
    filterFn: 'includesString',
  }),
  kindHelper.accessor('scored', {
    header: 'Videos',
    enableColumnFilter: false,
    meta: { filterType: 'none', align: 'left' },
  }),
  kindHelper.accessor('failPct', {
    header: 'Failing',
    enableColumnFilter: false,
    meta: { filterType: 'none', align: 'left' },
    cell: (info) => (
      <Pill tone={info.getValue() > 50 ? 'danger' : info.getValue() > 20 ? 'warn' : 'green'} dot>
        {info.getValue().toFixed(0)}%
      </Pill>
    ),
  }),
  kindHelper.accessor('meanPenalty', {
    header: 'Avg. penalty',
    enableColumnFilter: false,
    meta: { filterType: 'none', align: 'left' },
    cell: (info) => info.getValue().toFixed(1),
  }),
]);

function KindsView({ summary }: { summary: QualitySummary }) {
  return summary.byKind.length === 0 ? (
    <div className="empty-state">
      <div className="empty-state__title">No scored videos</div>
    </div>
  ) : (
    <DataTable columns={kindColumns} data={summary.byKind} getRowId={(k) => `${k.modelName}/${k.style}`} />
  );
}

const columnHelper = createDataTableColumns<QualityVideo>();

export default function AdminQualityPage() {
  const [summary, setSummary] = useState<QualitySummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [grade, setGrade] = useState<QualityGradeFilter | ''>('');
  const [sort, setSort] = useState<QualitySort>('worst');
  const [selected, setSelected] = useState<string | null>(null);
  // The open tab lives in the URL (?view=problems), so a link opens the right one.
  const [params, setParams] = useSearchParams();
  const view: QualityView = isView(params.get('view')) ? (params.get('view') as QualityView) : 'overview';
  const setView = (next: QualityView) =>
    setParams((prev) => {
      const out = new URLSearchParams(prev);
      out.set('view', next);
      return out;
    });

  const { items, loading, loadingMore, error, setError, hasMore, load, loadMore } = usePaginatedList<QualityVideo>(
    (nextOffset) =>
      listQualityVideos({
        grade: grade || null,
        sort,
        limit: PAGE_SIZE,
        offset: nextOffset,
      }),
  );

  useEffect(() => {
    let cancelled = false;
    getQualitySummary()
      .then((s) => !cancelled && setSummary(s))
      .catch((err: unknown) => !cancelled && setSummaryError(safeErrorMessage(err, 'Unable to load the summary.')));
    return () => {
      cancelled = true;
    };
  }, []);

  // Grade and sort are applied by the server (it holds every video, the page holds
  // one page of them), so changing either reloads the list from the first row.
  useEffect(() => {
    load(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grade, sort]);

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor((v) => formatDate(v.createdAt), {
          id: 'createdAt',
          header: 'Date',
          filterFn: 'includesString',
        }),
        columnHelper.accessor((v) => v.title || '(untitled)', {
          id: 'title',
          header: 'Title',
          filterFn: 'includesString',
          meta: { align: 'left' },
        }),
        columnHelper.accessor((v) => `${v.modelName ?? '—'} / ${v.style ?? '—'}`, {
          id: 'kind',
          header: 'Model / style',
          filterFn: 'includesString',
        }),
        columnHelper.accessor((v) => v.grade ?? 'unscored', {
          id: 'grade',
          header: 'Grade',
          enableSorting: false,
          enableColumnFilter: false,
          meta: { filterType: 'none' },
          cell: ({ row }) => <GradePill grade={row.original.grade} />,
        }),
        columnHelper.accessor((v) => v.reasons.slice(0, 2).map(describeReason).join(', '), {
          id: 'problems',
          header: 'Top problems',
          enableSorting: false,
          filterFn: 'includesString',
          meta: { align: 'left' },
          cell: (info) => info.getValue() || '—',
        }),
        columnHelper.display({
          id: 'actions',
          header: 'Actions',
          enableColumnFilter: false,
          meta: { filterType: 'none' },
          cell: ({ row }) => (
            <div className="admin-table-actions">
              <button
                type="button"
                className="button secondary admin-table-actions__compact"
                onClick={() => setSelected(row.original.jobId)}
              >
                Details
              </button>
            </div>
          ),
        }),
      ]),
    [],
  );

  return (
    <section className="admin-users">
      <AdminTabs />

      <div className="admin-header">
        <h1>Video Quality</h1>
        <p>Internal quality scores for every completed video. Not shown to users.</p>
      </div>

      {summaryError && <ErrorBanner message={summaryError} onDismiss={() => setSummaryError(null)} />}
      {summary && summary.scored === 0 && (
        <p className="quality-detail__note">
          No videos have been scored yet. Run <code>evals.quality.history --save</code> in valoi-engine-pipeline to
          score them.
        </p>
      )}
      <Segmented
        label="Video quality views"
        options={VIEWS}
        value={view}
        onChange={setView}
        className="quality-views"
      />

      {view !== 'videos' &&
        (summary ? (
          view === 'overview' ? (
            <OverviewView summary={summary} />
          ) : view === 'problems' ? (
            <ProblemsView summary={summary} />
          ) : (
            <KindsView summary={summary} />
          )
        ) : (
          !summaryError && (
            <div className="loading-container">
              <div className="loading-spinner" />
            </div>
          )
        ))}

      {view === 'videos' && (
        <>
          <div className="sli-toolbar quality-toolbar admin-section-gap">
            <label htmlFor="quality-grade">Grade</label>
            <select
              id="quality-grade"
              value={grade}
              onChange={(e) => setGrade(e.target.value as QualityGradeFilter | '')}
            >
              {GRADE_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
            <label htmlFor="quality-sort">Sort</label>
            <select id="quality-sort" value={sort} onChange={(e) => setSort(e.target.value as QualitySort)}>
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner" />
            </div>
          ) : error && items.length === 0 ? (
            <ErrorBanner message={error} />
          ) : items.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state__title">No videos match</div>
            </div>
          ) : (
            <>
              <DataTable
                columns={columns}
                data={items}
                getRowId={(v) => v.jobId}
                enablePagination={true}
                pageSize={20}
              />
              {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
              {hasMore && (
                <button
                  type="button"
                  className="button secondary wallet-activity__more"
                  onClick={loadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading…' : 'Load more from server'}
                </button>
              )}
            </>
          )}
        </>
      )}

      {selected && <QualityDetail jobId={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}
